import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, field, StringDecoder } from "@simonbackx/simple-encoding";
import { SimpleError } from "@simonbackx/simple-errors";
import { v4 as uuidv4 } from "uuid";
import { ResolutionRequest } from "../../files/ResolutionRequest";
export var RecordType;
(function (RecordType) {
    /**
     * Show a checkbox to the user so they can check the value on/off
     * It is possible to add a textarea as soon as the checkbox is selected if the extra property is set.
     */
    RecordType["Checkbox"] = "Checkbox";
    /**
     * Select one (or none if not required)
     */
    RecordType["ChooseOne"] = "ChooseOne";
    /**
     * Select one, zero or more from a menu
     * The way this is shown will differ depending on the minimum or maximum setting. e.g. when exactly one
     * item should be selected, we could show it with a dropdown menu
     */
    RecordType["MultipleChoice"] = "MultipleChoice";
    /**
     * Small text input
     */
    RecordType["Text"] = "Text";
    /**
     * Big text input
     */
    RecordType["Textarea"] = "Textarea";
    /**
     * Address input
     */
    RecordType["Address"] = "Address";
    RecordType["Phone"] = "Phone";
    RecordType["Email"] = "Email";
    RecordType["Date"] = "Date";
    RecordType["Price"] = "Price";
    RecordType["Image"] = "Image";
    RecordType["Integer"] = "Integer";
})(RecordType || (RecordType = {}));
export var RecordWarningType;
(function (RecordWarningType) {
    RecordWarningType["Info"] = "Info";
    RecordWarningType["Warning"] = "Warning";
    RecordWarningType["Error"] = "Error";
})(RecordWarningType || (RecordWarningType = {}));
export class RecordWarning extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.text = "";
        this.type = RecordWarningType.Info;
        /**
         * Show a warning if the associated value is falsy
         */
        this.inverted = false;
    }
    static get sort() {
        return (warning1, warning2) => {
            const priority1 = warning1.type;
            const priority2 = warning2.type;
            if (priority1 == RecordWarningType.Error && priority2 == RecordWarningType.Warning ||
                priority1 == RecordWarningType.Warning && priority2 == RecordWarningType.Info ||
                priority1 == RecordWarningType.Error && priority2 == RecordWarningType.Info) {
                return -1;
            }
            else if (priority1 == RecordWarningType.Info && priority2 == RecordWarningType.Warning ||
                priority1 == RecordWarningType.Warning && priority2 == RecordWarningType.Error ||
                priority1 == RecordWarningType.Info && priority2 == RecordWarningType.Error) {
                return 1;
            }
            else {
                return 0;
            }
        };
    }
    get icon() {
        switch (this.type) {
            case RecordWarningType.Error: return " exclamation-two red";
            case RecordWarningType.Warning: return " exclamation yellow";
            case RecordWarningType.Info: return " info-text";
        }
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], RecordWarning.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], RecordWarning.prototype, "text", void 0);
__decorate([
    field({ decoder: new EnumDecoder(RecordWarningType) })
], RecordWarning.prototype, "type", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], RecordWarning.prototype, "inverted", void 0);
export class RecordChoice extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = "";
        this.description = "";
        /**
         * Show a warning if selected (or not selected if inverted)
         */
        this.warning = null;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], RecordChoice.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], RecordChoice.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 118 })
], RecordChoice.prototype, "description", void 0);
__decorate([
    field({ decoder: RecordWarning, version: 122, nullable: true })
], RecordChoice.prototype, "warning", void 0);
export class RecordSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * Short name (used mainly for displaying the information)
         */
        this.name = "";
        /**
         * When used with checkbox: checkbox needs to get checked (e.g accept terms, confirm age...)
         * Multiple choice: minimum one selection required
         * Text: required input
         */
        this.required = false;
        /**
         * Whether you need permission to collect this information
         */
        this.sensitive = true;
        /**
         * Only used for checkboxes
         */
        this.askComments = false;
        /**
         * Future idea:
         * Is this information visible for the member?
         * -> needs some major changes in saving the encryption blobs (needs to get split in two)
         */
        // @field({ decoder: BooleanDecoder })
        // visibleForMembers = true
        this.type = RecordType.Text;
        /**
         * In case of multiple choice: the values you can choose from with optional additional information
         */
        this.choices = [];
        /**
        * Label used for input (depending on the type)
        * Checkbox: text next to checkbox
        * Text inputs: label field above the input
        * If empty: name is used
        */
        this.label = "";
        /**
         * Text underneath the label in case of a checkbox.
         * For other types: below the input
         */
        this.description = "";
        /// In case of textboxes or comments for checked checkboxes
        this.inputPlaceholder = "";
        /// Text below the input field for comments (if any)
        this.commentsDescription = "";
        /**
         * Show a warning if selected / entered (or not selected/entered if inverted)
         */
        this.warning = null;
    }
    validate(answers, requiredCategory = true) {
        const answer = answers.find(a => a.settings.id === this.id);
        if (!requiredCategory && (!answer || answer.isEmpty)) {
            // Okay to skip
            return;
        }
        if (this.required && !answer) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Dit veld is verplicht",
                field: this.id
            });
        }
        if (answer) {
            answer.settings = this;
            answer.validate();
        }
        return answer;
    }
    get excelColumns() {
        if (this.type === RecordType.Address) {
            return [
                this.name + ' - Straat en nummer',
                this.name + ' - Postcode',
                this.name + ' - Gemeente',
                this.name + ' - Land'
            ];
        }
        return [this.name];
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], RecordSettings.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], RecordSettings.prototype, "name", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], RecordSettings.prototype, "required", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 123 })
], RecordSettings.prototype, "sensitive", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 119 })
], RecordSettings.prototype, "askComments", void 0);
__decorate([
    field({ decoder: new EnumDecoder(RecordType) })
], RecordSettings.prototype, "type", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordChoice) })
], RecordSettings.prototype, "choices", void 0);
__decorate([
    field({ decoder: StringDecoder })
], RecordSettings.prototype, "label", void 0);
__decorate([
    field({ decoder: StringDecoder })
], RecordSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: StringDecoder })
], RecordSettings.prototype, "inputPlaceholder", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 120 })
], RecordSettings.prototype, "commentsDescription", void 0);
__decorate([
    field({ decoder: RecordWarning, version: 122, nullable: true })
], RecordSettings.prototype, "warning", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(ResolutionRequest), optional: true })
], RecordSettings.prototype, "resolutions", void 0);
//# sourceMappingURL=RecordSettings.js.map